// @ts-nocheck
/*
 * Port of v1 views/dashboard/manage-agents/AgentCallQualityModal.js (simple view),
 * wrapped in shadcn Dialog.
 *
 * Shows a verdict banner, five plain-language tiles and the worst calls.
 * Removed from the render (still collected + stored by the API): all 18
 * MetricCards with their info popovers, all three charts, and the "how to
 * read these numbers" threshold legend. Dialog width narrowed to match v1's
 * xl -> lg change.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { toast } from 'sonner';
import type { ColumnDef } from '@tanstack/react-table';
import { DataTable } from '@/components/data-table/DataTable';
import { getApiCall } from '@/service/httpCall';
import { callRowCustomerNumber } from '@/features/cti/ctiHelpers';
import { API_CALL_QUALITY_AGENT_METRICS } from '@/service/url';
import {
  WARNING_TEXT,
  buildSimpleMetrics,
  buildSimpleVerdict,
  mosQuality,
} from '@/utilities/callQualityRating';
import {
  QualityTile,
  QualityVerdict,
} from '@/components/partials/components/CallQualitySimple';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { DateRangeFilter } from '@/components/filters/DateRangeFilter';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

const selectCls =
  'input-embossed flex h-10 w-full rounded-md px-3 text-sm focus:outline-none';

const RANGE_PRESETS = [
  { key: 'today', label: 'Today', days: 0 },
  { key: '7d', label: 'Last 7 days', days: 6 },
  { key: '30d', label: 'Last 30 days', days: 29 },
  { key: 'month', label: 'This month', month: true },
  { key: 'custom', label: 'Custom range' },
];

const presetRange = (key: string) => {
  const end = moment().endOf('day');
  if (key === 'today') return { from: moment().startOf('day'), to: end };
  if (key === 'month') return { from: moment().startOf('month'), to: end };
  if (key === '30d') return { from: moment().subtract(29, 'days').startOf('day'), to: end };
  return { from: moment().subtract(6, 'days').startOf('day'), to: end };
};

const num = (v: any) => {
  if (v === null || v === undefined || v === '') return null;
  const x = Number(v);
  return Number.isFinite(x) ? x : null;
};

/** Quality as a coloured word — the MOS number stays in the DB, not on screen. */
const qualityCell = (row: any) => {
  const q = mosQuality(row.mos_avg);
  return <span style={{ color: q.color, fontWeight: 700 }}>{q.label}</span>;
};

const firstIssue = (row: any) => {
  const codes = Array.isArray(row.warnings)
    ? row.warnings.filter((c: any) => WARNING_TEXT[c])
    : [];
  return codes.length ? WARNING_TEXT[codes[0]] : '—';
};

function AgentCallQualityModal({ show, onHide, agentId, agentName, agentNumber }: any) {
  const [presetKey, setPresetKey] = useState('7d');
  const [customFrom, setCustomFrom] = useState(
    moment().subtract(6, 'days').format('YYYY-MM-DD'),
  );
  const [customTo, setCustomTo] = useState(moment().format('YYYY-MM-DD'));
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  const resolvedRange = useMemo(() => {
    if (presetKey === 'custom') {
      return {
        from: moment(customFrom).startOf('day'),
        to: moment(customTo).endOf('day'),
      };
    }
    return presetRange(presetKey);
  }, [presetKey, customFrom, customTo]);

  const fetchData = useCallback(async () => {
    if (!agentId) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({
        from: resolvedRange.from.format('YYYY-MM-DD HH:mm:ss'),
        to: resolvedRange.to.format('YYYY-MM-DD HH:mm:ss'),
        worst_limit: '10',
      });
      const resp = await getApiCall(
        `${API_CALL_QUALITY_AGENT_METRICS(agentId)}?${params.toString()}`,
        {},
      );
      setData(resp || null);
    } catch (e: any) {
      toast.error(typeof e === 'string' ? e : 'Failed to load call quality');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [agentId, resolvedRange]);

  useEffect(() => {
    if (show && agentId) fetchData();
    if (!show) setData(null);
  }, [show, agentId, fetchData]);

  const summary = data?.summary || null;
  const worstCalls = useMemo(
    () => (Array.isArray(data?.worst_calls) ? data.worst_calls : []),
    [data],
  );

  const calls = num(summary?.calls) || 0;
  const poorCalls = num(summary?.poor_calls) || 0;
  const poorPct = calls > 0 ? (poorCalls / calls) * 100 : 0;
  const verdict = useMemo(() => buildSimpleVerdict(summary), [summary]);

  const title = agentName || `Agent #${agentId}`;

  const worstColumns = useMemo<ColumnDef<any>[]>(
    () => [
      {
        id: 'started',
        header: 'Started',
        accessorFn: (row: any) => row.started_at || '',
        cell: ({ row }: any) =>
          row.original.started_at
            ? moment(row.original.started_at).format('DD MMM · HH:mm')
            : '—',
      },
      {
        id: 'number',
        header: 'Number',
        accessorFn: (row: any) => callRowCustomerNumber(row),
        cell: ({ row }: any) => callRowCustomerNumber(row.original) || '—',
      },
      {
        id: 'quality',
        header: 'Quality',
        accessorFn: (row: any) => num(row.mos_avg) ?? -1,
        cell: ({ row }: any) => qualityCell(row.original),
      },
      {
        id: 'issue',
        header: 'What went wrong',
        accessorFn: (row: any) => firstIssue(row),
      },
    ],
    [],
  );

  return (
    <Dialog open={show} onOpenChange={(next) => !next && onHide?.()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="text-base">
            Call Quality — {title}
            {agentNumber ? (
              <span style={{ fontSize: 13, opacity: 0.6 }}> ({agentNumber})</span>
            ) : null}
          </DialogTitle>
        </DialogHeader>

        <div className="max-h-[70vh] overflow-y-auto pr-1">
          {/* ---- Filters ---- */}
          <div className="mb-3 flex flex-wrap items-end gap-2">
            <div className="w-[160px]">
              <label className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Date range</label>
              <select
                className={selectCls}
                style={{ height: 36 }}
                value={presetKey}
                onChange={(e) => setPresetKey(e.target.value)}
              >
                {RANGE_PRESETS.map((p) => (
                  <option key={p.key} value={p.key}>
                    {p.label}
                  </option>
                ))}
              </select>
            </div>

            {presetKey === 'custom' ? (
              <DateRangeFilter
                compact
                value={{ from: customFrom, to: customTo }}
                onChange={({ from, to }) => {
                  setCustomFrom(from);
                  setCustomTo(to);
                }}
                max={moment().format('YYYY-MM-DD')}
                onApply={fetchData}
                applyLabel={loading ? 'Loading…' : 'Apply'}
                disabled={loading}
              />
            ) : (
              <Button className="h-9" onClick={fetchData} disabled={loading}>
                {loading ? 'Loading…' : 'Apply'}
              </Button>
            )}
          </div>

          <div className="mb-3 text-right" style={{ fontSize: 11, opacity: 0.6 }}>
            {resolvedRange.from.format('DD MMM YYYY')} –{' '}
            {resolvedRange.to.format('DD MMM YYYY')}
            {' · '}
            {calls} measured call{calls === 1 ? '' : 's'}
          </div>

          {loading ? (
            <div className="py-4 text-center text-sm text-muted-foreground">
              Loading call quality…
            </div>
          ) : calls === 0 ? (
            <div
              style={{
                border: '1px dashed #81818155',
                borderRadius: 10,
                padding: 18,
                fontSize: 13,
                lineHeight: 1.5,
              }}
            >
              <strong>No data for this range.</strong>
              <div style={{ marginTop: 6, opacity: 0.8 }}>
                Quality is measured only on calls made from the browser softphone. If
                this agent works on a mobile profile there is nothing to measure here.
              </div>
            </div>
          ) : (
            <>
              {/* ---- What to read first ---- */}
              <QualityVerdict verdict={verdict} style={{ marginBottom: 14 }} />

              {/* ---- The only numbers shown ---- */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 14 }}>
                <QualityTile
                  label="Calls with problems"
                  value={`${poorCalls}`}
                  sub={`of ${calls} call${calls === 1 ? '' : 's'}`}
                  desc="Calls that did not sound good enough for the person on the other end."
                  color={poorPct > 15 ? '#ef4444' : poorPct > 5 ? '#f59e0b' : '#10b981'}
                  status={poorPct <= 5 ? 'good' : 'warn'}
                />
                {buildSimpleMetrics(summary).map((m: any) => (
                  <QualityTile key={m.key} {...m} />
                ))}
              </div>

              {/* ---- Worst calls ---- */}
              {worstCalls.length ? (
                <div style={{ width: '100%' }}>
                  <div
                    style={{ fontSize: 11, fontWeight: 700, opacity: 0.6, marginBottom: 4 }}
                  >
                    CALLS THAT DID NOT SOUND GOOD
                  </div>
                  <DataTable
                    tableId="agent-call-quality.worst"
                    columns={worstColumns}
                    data={worstCalls}
                    pageSize={5}
                    emptyTitle="No calls"
                    disableExport
                  />
                </div>
              ) : null}
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="secondary" size="sm" onClick={onHide}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default AgentCallQualityModal;
